import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Edit, Trash2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@aaas/shared';
import type { PipelineRound } from '../types';

interface SortableRoundItemProps {
  round: PipelineRound;
  onEdit: (round: PipelineRound) => void;
  onDelete: (round: PipelineRound) => void;
  isDeleting?: boolean;
}

export function SortableRoundItem({
  round,
  onEdit,
  onDelete,
  isDeleting = false,
}: SortableRoundItemProps) {
  const { t } = useTranslation('ats');
  const isDefaultRound =
    round.round_type === 'application_received' || round.round_type === 'hired';
  const roundTypeLabel =
    t(`pipeline.roundTypes.${round.round_type}`, { defaultValue: '' }) || round.round_type;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: round.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg
        ${isDragging ? 'opacity-50 shadow-lg z-50' : ''}
        ${isDeleting ? 'opacity-50' : ''}
      `}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className={`p-1 focus:outline-none ${
          isDeleting || isDefaultRound
            ? 'cursor-not-allowed text-gray-300'
            : 'cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600'
        }`}
        disabled={isDeleting || isDefaultRound}
        title={isDefaultRound ? t('pipeline.roundItem.moveDefaultTitle') : ''}
      >
        <GripVertical className="h-5 w-5" />
      </button>

      {/* Order Number */}
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
          isDefaultRound ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
        }`}
      >
        {round.order + 1}
      </div>

      {/* Round Name */}
      <div className="flex-grow min-w-0">
        <span className="font-medium text-gray-900 truncate block">{round.name}</span>
      </div>

      {/* Round Type Badge */}
      <div className="flex-shrink-0">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          {roundTypeLabel}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(round)}
          disabled={isDeleting}
          className="h-8 w-8 text-gray-500 hover:text-gray-700"
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(round)}
          disabled={isDeleting || isDefaultRound}
          className="h-8 w-8 text-gray-500 hover:text-red-600"
          title={isDefaultRound ? t('pipeline.roundItem.deleteDefaultTitle') : ''}
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
